import { buildApiPayload } from '../../shared/utils/buildApiPayload';
import { convertParams } from './convertParams';
import { AuthenticateError } from '../../shared/types/authErrors';
import { createAccount, getAuthorizeUrl, getAuthorizeForReconnectUrl, reconnectAccount, migrationReconnectAccount } from '../api/UnifiedAccountApi';
export function resolveOAuthScopeSet(oAuthScope, oAuthScopes) {
  if (oAuthScopes !== undefined) {
    return oAuthScopes;
  }
  if (!oAuthScope) {
    return undefined;
  }
  const scopes = oAuthScope.split(/[\s,]+/).filter(Boolean);
  return scopes.length > 0 ? scopes : undefined;
}
export function prepareOAuthParams(oAuthParams) {
  return Object.assign({
    state: oAuthParams.state
  }, oAuthParams.scopeSet && {
    scopeSet: oAuthParams.scopeSet
  }, oAuthParams.mcpServerUrl && {
    mcpServerUrl: oAuthParams.mcpServerUrl
  }, oAuthParams.clientId && {
    clientId: oAuthParams.clientId
  });
}
export function prepareAccountRequest(payload, appId) {
  return Object.assign({
    appId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams)
  }, payload.oAuthParams && {
    oauthParams: prepareOAuthParams(payload.oAuthParams)
  });
}
export function prepareReconnectAccountRequest(payload, accountId) {
  return Object.assign({
    accountId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams)
  }, payload.oAuthParams && {
    oauthParams: prepareOAuthParams(payload.oAuthParams)
  });
}
export function createBuildAuthPayload(appId) {
  return function buildAuthPayloadFn(params) {
    return Promise.resolve(buildApiPayload(params.sessionId, params.steps, params.urlParams, params.oAuthParams, params.oAuthState, params.oAuthScopeSet, params.mcpParams, appId));
  };
}
export function createAuthorizeUrlFetcher({
  portalId,
  appId,
  sessionId
}) {
  return async function makeAuthorizeUrlDescriptor(payload) {
    const response = await getAuthorizeUrl(portalId, Object.assign({
      appId,
      sessionId,
      inputParams: convertParams(payload.inputParams)
    }, payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams)
    }));
    if (response['@result'] !== 'OK') {
      var _response$upstreamErr;
      throw new AuthenticateError(response.localizedErrorMessage, (_response$upstreamErr = response.upstreamError) === null || _response$upstreamErr === void 0 ? void 0 : _response$upstreamErr.upstreamErrorMessageInterpretation);
    }
    return {
      authorizeUrl: response.authorizeUrl
    };
  };
}
export function createReconnectAuthorizeUrlFetcher({
  portalId,
  accountId,
  sessionId
}) {
  return async function makeAuthorizeUrlDescriptor(payload) {
    const response = await getAuthorizeForReconnectUrl(portalId, Object.assign({
      accountId,
      sessionId,
      inputParams: convertParams(payload.inputParams)
    }, payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams)
    }));
    if (response['@result'] !== 'OK') {
      var _response$upstreamErr2;
      throw new AuthenticateError(response.localizedErrorMessage, (_response$upstreamErr2 = response.upstreamError) === null || _response$upstreamErr2 === void 0 ? void 0 : _response$upstreamErr2.upstreamErrorMessageInterpretation);
    }
    return {
      authorizeUrl: response.authorizeUrl
    };
  };
}
export function createFinishNewAccountHandler({
  portalId,
  appId
}) {
  return async function finishAuthFlow(payload) {
    const request = prepareAccountRequest(payload, appId);
    const response = await createAccount(portalId, request);
    if (response['@result'] !== 'OK') {
      var _response$upstreamErr3;
      throw new AuthenticateError(response.localizedErrorMessage, (_response$upstreamErr3 = response.upstreamError) === null || _response$upstreamErr3 === void 0 ? void 0 : _response$upstreamErr3.upstreamErrorMessageInterpretation);
    }
    return {
      accountId: response.accountId
    };
  };
}
export function createFinishReconnectHandler({
  portalId,
  accountId
}) {
  return async function finishAuthFlow(payload) {
    const request = prepareReconnectAccountRequest(payload, accountId);
    const response = await reconnectAccount(portalId, request);
    if (response['@result'] !== 'OK') {
      var _response$upstreamErr4;
      throw new AuthenticateError(response.localizedErrorMessage, (_response$upstreamErr4 = response.upstreamError) === null || _response$upstreamErr4 === void 0 ? void 0 : _response$upstreamErr4.upstreamErrorMessageInterpretation);
    }
    return {
      accountId
    };
  };
}
export function prepareMigrationReconnectRequest(payload, appId, oAuthorAccountId) {
  return Object.assign({
    appId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams)
  }, payload.oAuthParams && {
    oauthParams: prepareOAuthParams(payload.oAuthParams)
  }, oAuthorAccountId && {
    oAuthorAccountId
  });
}
export function createFinishMigrationReconnectHandler({
  portalId,
  appId,
  oAuthorAccountId
}) {
  return async function finishAuthFlow(payload) {
    const request = prepareMigrationReconnectRequest(payload, appId, oAuthorAccountId);
    const response = await migrationReconnectAccount(portalId, request);
    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(response.localizedErrorMessage, undefined);
    }
    return {
      accountId: response.accountId
    };
  };
}